import React, { useEffect, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import type { useUnifiedEngagementReview } from '../../../hooks/advisory/useUnifiedEngagementReview';
import {
  hasReviewPreparationFailure,
  type ReviewProgressStage,
} from '../../../hooks/advisory/advisoryReview';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import { meta } from '../../../styles/visualLanguage';
import { AdvisoryIntakeScreen } from './AdvisoryIntakeScreen';

/** Recovery of the initial request stays beside the submitted decision. */
export function AdvisoryReviewInitialPreparation({
  review,
  question,
  progressStage,
  animateEntry,
  readFailure,
  onReload,
}: {
  review: ReturnType<typeof useUnifiedEngagementReview>;
  question: string;
  progressStage?: ReviewProgressStage;
  animateEntry?: boolean;
  readFailure?: 'denied' | 'failed';
  onReload?: () => void;
}) {
  const { t } = useUntypedTranslation();
  const [recoverable, setRecoverable] = useState(false);
  const reloading = useRef(false);
  if (review.readStatus === 'ready' || review.readStatus === 'absent') reloading.current = false;
  useEffect(() => {
    setRecoverable(false);
    if (review.busy || review.snapshot?.status !== 'preparing') return;
    const timer = window.setTimeout(() => setRecoverable(true), 30000);
    return () => window.clearTimeout(timer);
  }, [review.busy, review.snapshot?.status, review.snapshot?.revision]);
  const readError = readFailure || (review.readError ? review.readStatus : null);
  const waitingForReload = reloading.current && review.readStatus === 'loading';
  const showReconcile = !readError && !waitingForReload && review.stale && !review.busy;
  const failed = Boolean(review.error || hasReviewPreparationFailure(review.snapshot));
  const showRetry =
    !readError && !waitingForReload && !review.stale && (failed || recoverable) && !review.busy;
  const message = readError
    ? readError === 'denied'
      ? 'advisory:review.readDenied'
      : readError === 'invalid'
      ? 'advisory:review.readInvalid'
      : 'advisory:review.readFailed'
    : showReconcile
    ? 'advisory:review.changedElsewhere'
    : showRetry && failed
    ? 'advisory:review.operationFailed'
    : null;
  return (
    <AdvisoryIntakeScreen
      question={question}
      readOnly
      busy={!readError && !showReconcile && !showRetry}
      stage={progressStage}
      animateEntry={animateEntry}
      actionLabel={
        readError
          ? 'advisory:review.readRetry'
          : showReconcile
          ? 'advisory:review.useSavedDraft'
          : 'advisory:intake.tryAgain'
      }
      actionTestId={
        readError
          ? 'advisory-review-reload'
          : showReconcile
          ? 'advisory-review-reconcile'
          : showRetry
          ? 'advisory-review-retry'
          : undefined
      }
      onSubmit={
        readError
          ? () => {
              reloading.current = true;
              (onReload || review.retryRead)();
            }
          : showReconcile
          ? () => review.reconcile(false)
          : showRetry
          ? review.retry
          : undefined
      }
    >
      {message && (
        <Typography
          role="alert"
          data-testid={
            readError
              ? 'advisory-review-read-error'
              : showReconcile
              ? 'advisory-review-stale'
              : undefined
          }
          sx={[meta]}
        >
          {t(message)}
        </Typography>
      )}
    </AdvisoryIntakeScreen>
  );
}
